import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export interface Notice {
  kind: "success" | "error";
  text: string;
}

const KIND_CLASSES: Record<Notice["kind"], string> = {
  success: "border-green-600/50 bg-green-50 text-green-900 dark:bg-green-950 dark:text-green-100",
  error: "bg-background",
};

const SUCCESS_DISMISS_MS = 6000;

interface NoticeAlertProps {
  notice: Notice | null;
  onDismiss: () => void;
  /** Float at the top right (toast-like) so it stays visible wherever the page is scrolled. */
  floating?: boolean;
}

/** Dismissible success/error banner for the result of a page action. */
const NoticeAlert: React.FC<NoticeAlertProps> = ({ notice, onDismiss, floating = false }) => {
  const autoDismiss = floating && notice?.kind === "success";
  useEffect(() => {
    if (!autoDismiss) return;
    const id = window.setTimeout(onDismiss, SUCCESS_DISMISS_MS);
    return () => window.clearTimeout(id);
  }, [autoDismiss, notice, onDismiss]);

  if (!notice) return null;
  const alert = (
    <Alert
      variant={notice.kind === "error" ? "destructive" : "default"}
      // Success is announced politely; an error interrupts and stays until dismissed.
      role={notice.kind === "error" ? "alert" : "status"}
      aria-live={notice.kind === "error" ? "assertive" : "polite"}
      className={cn(
        "pr-10",
        floating
          ? "fixed inset-x-4 top-4 z-50 w-auto shadow-lg sm:left-auto sm:max-w-sm"
          : "relative mb-4",
        KIND_CLASSES[notice.kind]
      )}
    >
      <AlertDescription>{notice.text}</AlertDescription>
      <Button
        variant="ghost"
        size="icon"
        className="absolute right-2 top-2 h-6 w-6"
        aria-label="Dismiss"
        onClick={onDismiss}
      >
        <X className="h-4 w-4" />
      </Button>
    </Alert>
  );
  // The page wrapper animates with a transform, which would pin `fixed` to it instead
  // of the viewport; render floating notices at the document root.
  return floating ? createPortal(alert, document.body) : alert;
};

export default NoticeAlert;
